import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "conslog_recent_projects"
const MAX_RECENT = 5
const DEBOUNCE_MS = 250
const CHECK_SVG = '<svg class="w-4 h-4 text-brand flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/></svg>'

/**
 * Project switcher popover in the breadcrumb.
 *
 * Loads projects lazily via JSON on first open. Supports search (server-side,
 * debounced), infinite scroll, recent projects (localStorage + server fetch),
 * keyboard navigation (↑↓ Enter Esc), and ⌘J global shortcut.
 */
export default class extends Controller {
  static targets = ["panel", "input", "list", "scrollArea", "recentSection", "recentList", "emptyState", "spinner"]
  static values  = { currentProjectId: Number, currentPhase: String, url: String }

  connect() {
    this._onClickOutside  = this._onClickOutside.bind(this)
    this._onGlobalKeydown = this._onGlobalKeydown.bind(this)
    this._onDropdownClose = (e) => { if (e.detail?.except !== this) this._close() }
    this._activeIndex     = -1
    this._navigableItems  = []
    this._page            = 0
    this._hasMore         = false
    this._loading         = false
    this._cachedData      = null
    this._currentPhaseGroup = null

    this._trackVisit(this.currentProjectIdValue)

    document.addEventListener("keydown",        this._onGlobalKeydown)
    document.addEventListener("dropdown:close", this._onDropdownClose)
  }

  disconnect() {
    clearTimeout(this._closeTimer)
    clearTimeout(this._searchTimer)
    this._disconnectObserver()
    document.removeEventListener("click",          this._onClickOutside)
    document.removeEventListener("keydown",        this._onGlobalKeydown)
    document.removeEventListener("dropdown:close", this._onDropdownClose)
  }

  // ── Actions ────────────────────────────────────────────────────────────

  toggle(event) {
    event.stopPropagation()
    if (this.panelTarget.classList.contains("hidden")) this._open()
    else this._close()
  }

  search() {
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      const query = this.inputTarget.value.trim()
      if (!query && this._cachedData) {
        this._renderFromCache()
        return
      }
      this._fetchPage(1)
    }, DEBOUNCE_MS)
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":  event.preventDefault(); this._navigate(1);  break
      case "ArrowUp":    event.preventDefault(); this._navigate(-1); break
      case "Enter":      event.preventDefault(); this._selectActive(); break
      case "Escape":     event.preventDefault(); this._close(); break
    }
  }

  // ── Open / Close ───────────────────────────────────────────────────────

  _open() {
    clearTimeout(this._closeTimer)
    document.dispatchEvent(new CustomEvent("dropdown:close", { detail: { except: this } }))

    const panel = this.panelTarget
    panel.classList.remove("hidden")
    panel.offsetHeight // reflow
    panel.classList.remove("opacity-0", "scale-95")
    panel.classList.add("opacity-100", "scale-100")

    this.inputTarget.value = ""
    this.inputTarget.focus()

    if (this._cachedData) {
      this._renderFromCache()
    } else {
      this._fetchPage(1)
    }

    document.addEventListener("click", this._onClickOutside)
  }

  _close() {
    const panel = this.panelTarget
    if (panel.classList.contains("hidden")) return

    panel.classList.remove("opacity-100", "scale-100")
    panel.classList.add("opacity-0", "scale-95")
    this._closeTimer = setTimeout(() => panel.classList.add("hidden"), 150)
    this._activeIndex = -1

    document.removeEventListener("click", this._onClickOutside)
  }

  // ── Fetch ──────────────────────────────────────────────────────────────

  async _fetchPage(page) {
    if (this._loading) return
    this._loading = true

    const query = this.inputTarget.value.trim()
    const isInitial = page === 1 && !query
    const params = new URLSearchParams({ page, q: query })

    if (isInitial) {
      const recentIds = this._getRecentIds().filter(id => id !== this.currentProjectIdValue)
      if (recentIds.length) params.set("recent_ids", recentIds.join(","))
      params.set("current_id", this.currentProjectIdValue)
    }

    if (page === 1) this.spinnerTarget.classList.remove("hidden")

    try {
      const res  = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" } })
      const data = await res.json()

      if (page === 1) {
        this._clearList()
        this._renderRecent(isInitial ? (data.recent_projects || []) : [])
      }

      this._renderProjects(data.projects, page === 1 && query)
      this._hasMore = data.has_more
      this._page = page

      if (this._hasMore) this._setupScrollObserver()
      else this._disconnectObserver()

      // Cache initial data (accumulate on scroll)
      if (isInitial) {
        if (page === 1) {
          this._cachedData = { projects: data.projects, recent: data.recent_projects || [], hasMore: data.has_more }
        } else {
          this._cachedData.projects = [...this._cachedData.projects, ...data.projects]
          this._cachedData.hasMore = data.has_more
        }
      }
      // Cache additional pages when browsing (no query)
      if (!query && page > 1 && this._cachedData) {
        this._cachedData.projects = [...this._cachedData.projects, ...data.projects]
        this._cachedData.hasMore = data.has_more
      }

      this._refreshNavigable()
      this._activeIndex = -1
    } catch (e) {
      console.error("Project switcher fetch failed:", e)
    } finally {
      this._loading = false
      this.spinnerTarget.classList.add("hidden")
    }
  }

  _loadMore() {
    if (this._loading || !this._hasMore) return
    this._fetchPage(this._page + 1)
  }

  // ── Render ─────────────────────────────────────────────────────────────

  _clearList() {
    this.listTarget.innerHTML = ""
    this._currentPhaseGroup = null
    this.recentSectionTarget.classList.add("hidden")
    this.emptyStateTarget.classList.add("hidden")
  }

  _renderFromCache() {
    this._clearList()
    this._renderRecent(this._cachedData.recent)
    this._renderProjects(this._cachedData.projects, false)
    this._hasMore = this._cachedData.hasMore
    this._page = Math.ceil(this._cachedData.projects.length / 10) || 1
    if (this._hasMore) this._setupScrollObserver()
    else this._disconnectObserver()
    this._refreshNavigable()
    this._activeIndex = -1
  }

  _renderRecent(projects) {
    this.recentListTarget.innerHTML = ""
    if (!projects.length) { this.recentSectionTarget.classList.add("hidden"); return }
    projects.forEach(p => this.recentListTarget.appendChild(this._buildItem(p)))
    this.recentSectionTarget.classList.remove("hidden")
  }

  _renderProjects(projects, showEmptyState) {
    if (!projects.length) {
      if (showEmptyState) this.emptyStateTarget.classList.remove("hidden")
      return
    }
    projects.forEach(p => {
      if (p.phase !== this._currentPhaseGroup) {
        this._currentPhaseGroup = p.phase
        const hdr = document.createElement("div")
        hdr.className = "px-3 pt-2.5 pb-1"
        hdr.innerHTML = `<span class="text-[13px] font-semibold text-gray-400 uppercase tracking-wide">${this._esc(p.phase_label)}</span>`
        this.listTarget.appendChild(hdr)
      }
      this.listTarget.appendChild(this._buildItem(p))
    })
  }

  _buildItem(project) {
    const cur = project.id === this.currentProjectIdValue
    const a   = document.createElement("a")
    a.href = this._buildUrl(project)
    a.dataset.projectLink = ""
    a.dataset.projectId   = project.id
    a.className = `flex items-center gap-2 px-3 py-2 text-[13px] transition-colors cursor-pointer ${cur ? "bg-brand-50/60" : "hover:bg-gray-50"}`

    let h = ""
    if (project.status_label && project.status_class)
      h += `<span class="inline-flex items-center px-1.5 py-px rounded text-[11px] font-medium leading-tight whitespace-nowrap flex-shrink-0 ${this._esc(project.status_class)}">${this._esc(project.status_label)}</span>`
    h += `<span class="truncate ${cur ? "text-brand-700 font-medium" : "text-gray-800"}">${this._esc(project.name)}</span>`
    h += `<span class="flex-1"></span>`
    if (project.number)
      h += `<span class="text-[13px] text-gray-400 flex-shrink-0 tabular-nums">${this._esc(project.number)}</span>`
    if (cur) h += CHECK_SVG
    a.innerHTML = h
    return a
  }

  _buildUrl(project) {
    if (project.phase === this.currentPhaseValue)
      return window.location.pathname.replace(`/projects/${this.currentProjectIdValue}`, `/projects/${project.id}`)
    return `/projects/${project.id}/dashboard`
  }

  // ── Infinite scroll ────────────────────────────────────────────────────

  _setupScrollObserver() {
    this._disconnectObserver()
    if (!this._sentinel) {
      this._sentinel = document.createElement("div")
      this._sentinel.className = "h-1"
    }
    this.listTarget.appendChild(this._sentinel)

    this._observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) this._loadMore() },
      { root: this.scrollAreaTarget, threshold: 0.1 }
    )
    this._observer.observe(this._sentinel)
  }

  _disconnectObserver() {
    if (this._observer) { this._observer.disconnect(); this._observer = null }
  }

  // ── Keyboard nav ───────────────────────────────────────────────────────

  _navigate(direction) {
    this._refreshNavigable()
    if (!this._navigableItems.length) return
    this._activeIndex += direction
    if (this._activeIndex < 0) this._activeIndex = 0
    if (this._activeIndex >= this._navigableItems.length) this._activeIndex = this._navigableItems.length - 1
    this._highlightActive()
  }

  _selectActive() {
    if (this._activeIndex >= 0 && this._activeIndex < this._navigableItems.length) {
      const item = this._navigableItems[this._activeIndex]
      const url = item.getAttribute("href")
      if (url) {
        const id = parseInt(item.dataset.projectId)
        if (id) this._trackVisit(id)
        window.location.href = url
      }
    }
  }

  _highlightActive() {
    this._navigableItems.forEach((item, i) => {
      item.classList.toggle("!bg-brand-50", i === this._activeIndex)
      if (i === this._activeIndex) item.scrollIntoView({ block: "nearest" })
    })
  }

  _refreshNavigable() {
    this._navigableItems = Array.from(
      this.panelTarget.querySelectorAll("[data-project-link]")
    ).filter(el => el.offsetParent !== null)
  }

  // ── Recent projects ────────────────────────────────────────────────────

  _trackVisit(projectId) {
    if (!projectId) return
    let ids = this._getRecentIds()
    ids = ids.filter(id => id !== projectId)
    ids.unshift(projectId)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)))
  }

  _getRecentIds() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]") }
    catch { return [] }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  _esc(str) {
    if (!str) return ""
    const d = document.createElement("div")
    d.textContent = str
    return d.innerHTML
  }

  _onClickOutside(event) {
    if (!this.element.contains(event.target)) this._close()
  }

  _onGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "j") {
      event.preventDefault()
      if (this.panelTarget.classList.contains("hidden")) this._open()
      else this._close()
    }
  }
}
